import { Box } from '@lifeomic/chroma-react/components/Box';
import { defineMessages, useIntl } from 'react-intl';
import {
  File,
  User,
  Procedure,
  MoreHorizontal,
  MapPin,
} from '@lifeomic/chromicons';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { ButtonLink } from '@lifeomic/chroma-react/components/ButtonLink';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { useABAC } from '@lifeomic/phc-web-toolkit/src/hooks/useABAC';
import * as React from 'react';
import urls from '../../../src/util/urls';
import UserGuideLink from '@lifeomic/phc-web-toolkit/src/components/DetailsHeader/UserGuideLink';
import FileService from '@lifeomic/phc-web-toolkit/src/services/FileService';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import {
  RadioGroupMinimal,
  Radio,
} from '@lifeomic/chroma-react/components/Radio';
import { ViewMode } from './useAutoQueryParam';
import { Menu, MenuItem } from '@lifeomic/chroma-react/components/Menu';
import clsx from 'clsx';

const fileService = new FileService();

const messages = defineMessages({
  biomarkers: {
    id: 'omicsExplorer.navigationActions.biomarkers',
    defaultMessage: 'Biomarkers',
  },
  viewSwitcher: {
    id: 'omicsExplorer.navigationActions.viewSwitcher',
    defaultMessage: 'View switcher',
  },
  moreMenu: {
    id: 'omicsExplorer.navigationActions.moreMenu',
    defaultMessage: 'More menu',
  },
  moreActions: {
    id: 'omicsExplorer.navigationActions.moreActions',
    defaultMessage: 'More actions',
  },
  explorer: {
    id: 'omicsExplorer.navigationActions.explorer',
    defaultMessage: 'Explorer',
  },
  summary: {
    id: 'omicsExplorer.navigationActions.summary',
    defaultMessage: 'Summary',
  },
  tooltipReportPDF: {
    id: 'omicsExplorer.navigationActions.tooltipReportPDF',
    defaultMessage: 'View PDF',
  },
  tooltipSubjectViewer: {
    id: 'omicsExplorer.navigationActions.tooltipSubjectViewer',
    defaultMessage: 'Subject Viewer',
  },
  tooltipTestDetails: {
    id: 'omicsExplorer.navigationActions.tooltipTestDetails',
    defaultMessage: 'View Test Details',
  },
  viewPDFError: {
    id: 'omicsExplorer.navigationActions.viewPDFError',
    defaultMessage: 'Error loading PDF',
  },
});

const useStyles = makeStyles(theme => ({
  root: {},
  iconBtnsBox: {
    background: 'rgba(132,137,166,0.15)',
    borderRadius: theme.pxToRem(32),
    height: theme.pxToRem(35),
    padding: theme.spacing(0, 1.25),
  },
  toggle: {
    marginLeft: theme.spacing(2),
    position: 'relative',
    top: theme.pxToRem(1),
  },
  menuItem: {
    '& > a, & > button': {
      fontSize: theme.pxToRem(14),
      fontWeight: 'normal',
      padding: 0,
      '&, &:hover, &:focus': {
        color: '#000',
      },
      '&[aria-label="Help"]': {
        position: 'relative',
        left: theme.pxToRem(-2),
      },
    },
  },
  biomarkersPresent: {
    '&:after': {
      backgroundColor: theme.palette.orange.main,
      borderRadius: 5,
      content: "''",
      height: theme.spacing(1.1),
      position: 'absolute',
      right: 10,
      top: 8,
      width: theme.spacing(1.1),
    },
  },
  biomarkersNotable: {
    '&:after': {
      backgroundColor: theme.palette.red.main,
    },
  },
}));

export type TestBiomarkerStatus = 'absent' | 'present' | 'notable';

/** An entry in the "more actions" menu, supplied by whoever owns its behavior. */
type MoreMenuAction = {
  disabled: boolean;
  icon: NonNullable<React.ComponentProps<typeof Button>['icon']>;
  label: string;
  onClick: () => void;
};

interface NavigationActionsProps {
  setIsSlideOverExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  viewMode: ViewMode;
  onViewModeChange: (viewMode: ViewMode) => void;
  reportFileId?: string;
  subjectId?: string;
  testId?: string;
  testBiomarkerStatus: TestBiomarkerStatus;
  hideViewSwitcher?: boolean;
  genomicsReportAction?: MoreMenuAction;
  genomicFileVisibilityAction?: MoreMenuAction;
}

export const NavigationActions: React.FC<NavigationActionsProps> = ({
  setIsSlideOverExpanded,
  viewMode,
  onViewModeChange,
  reportFileId,
  subjectId,
  testId,
  testBiomarkerStatus,
  hideViewSwitcher = false,
  genomicsReportAction,
  genomicFileVisibilityAction,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [notify] = useNotify();
  const [fetchLink] = useService(fileService.fetchOpenLink);

  const fileClick = React.useCallback(async () => {
    setIsLoading(true);
    try {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const result = await fetchLink(reportFileId);
      const downloadUrl = result?.data?.downloadUrl;
      if (!downloadUrl) throw new Error('No download url for file');
      window.open(downloadUrl, '_blank');
    } catch (e) {
      console.error(e);
      notify(formatMessage(messages.viewPDFError), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [reportFileId, fetchLink, formatMessage, notify]);

  const [canPerform, permissions] = useABAC();

  return (
    <Box className={classes.root} justify="flex-end" gap={1.125}>
      <Box className={classes.iconBtnsBox}>
        <>
          {reportFileId && canPerform(permissions.downloadFile) && (
            <Tooltip
              placement="bottom"
              title={formatMessage(messages.tooltipReportPDF)}
            >
              <IconButton
                aria-label={formatMessage(messages.tooltipReportPDF)}
                icon={File}
                onClick={fileClick}
                size={0}
                disabled={isLoading}
              />
            </Tooltip>
          )}
          <Tooltip title={formatMessage(messages.biomarkers)}>
            <IconButton
              size={0}
              aria-label={formatMessage(messages.biomarkers)}
              icon={MapPin}
              disabled={!testId}
              className={clsx({
                [classes.biomarkersPresent]: testBiomarkerStatus !== 'absent',
                [classes.biomarkersNotable]: testBiomarkerStatus === 'notable',
              })}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setIsSlideOverExpanded(prevValue => !prevValue);
              }}
            />
          </Tooltip>
          <Menu
            aria-label={formatMessage(messages.moreMenu)}
            anchorElement={
              <IconButton
                size={0}
                aria-label={formatMessage(messages.moreActions)}
                icon={MoreHorizontal}
              />
            }
            items={[
              <React.Fragment key="ActionItems">
                <MenuItem className={classes.menuItem}>
                  <ButtonLink
                    to={urls.app.patients(subjectId)}
                    icon={User}
                    variant="text"
                  >
                    {formatMessage(messages.tooltipSubjectViewer)}
                  </ButtonLink>
                </MenuItem>
                {testId && (
                  <MenuItem className={classes.menuItem}>
                    <ButtonLink
                      icon={Procedure}
                      target="_blank"
                      rel="noopener noreferrer"
                      to={urls.app.omics.tests(testId)}
                      variant="text"
                    >
                      {formatMessage(messages.tooltipTestDetails)}
                    </ButtonLink>
                  </MenuItem>
                )}
                {genomicsReportAction && (
                  <MenuItem className={classes.menuItem}>
                    <Button
                      variant="text"
                      icon={genomicsReportAction.icon}
                      disabled={genomicsReportAction.disabled}
                      onClick={genomicsReportAction.onClick}
                    >
                      {genomicsReportAction.label}
                    </Button>
                  </MenuItem>
                )}
                {genomicFileVisibilityAction && (
                  <MenuItem className={classes.menuItem}>
                    <Button
                      variant="text"
                      icon={genomicFileVisibilityAction.icon}
                      disabled={genomicFileVisibilityAction.disabled}
                      onClick={genomicFileVisibilityAction.onClick}
                    >
                      {genomicFileVisibilityAction.label}
                    </Button>
                  </MenuItem>
                )}
                <MenuItem className={classes.menuItem}>
                  <UserGuideLink name="omicsExplorer" />
                </MenuItem>
              </React.Fragment>,
            ]}
          />
        </>
      </Box>
      {!hideViewSwitcher && (
        <RadioGroupMinimal
          aria-label={formatMessage(messages.viewSwitcher)}
          value={viewMode}
          onChange={e => {
            onViewModeChange(e.target.value as ViewMode);
          }}
        >
          <Radio label={formatMessage(messages.explorer)} value="DEFAULT" />
          <Radio label={formatMessage(messages.summary)} value="SUMMARY" />
        </RadioGroupMinimal>
      )}
    </Box>
  );
};
